import React, { useEffect, useState, useRef } from "react";
import { Check, X, ChevronDown } from "lucide-react"; // Using lucide-react instead of react-icons
import useAdminsApi from "../api/useAdminsApi";
import { toast } from "react-toastify";

const RolePermissionModule = () => {
    const { addAdmin, getAdmins, loading, updateAdmin, updatePermissions } = useAdminsApi();
    const [users, setUsers] = useState([]);
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [total_pages, setTotalPages] = useState(1);
    const [selectedUser, setSelectedUser] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [dropdownSearchTerm, setDropdownSearchTerm] = useState('');
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [permissions, setPermissions] = useState([]);
    const [ipAddresses, setIpAddresses] = useState([]);
    const [ipInput, setIpInput] = useState("");
    const ipInputRef = useRef(null);

    const InitialPermission = [
        {
            "screen": "Dashboard",
            "allowed": true,
            "actions": []
        },
        {
            "screen": "Users",
            "allowed": true,
            "actions": []
        },
        {
            "screen": "Leads",
            "allowed": true,
            "actions": [
                {
                    "action": "edit",
                    "allowed": true
                }
            ]
        },
        {
            "screen": "Tickets",
            "allowed": true,
            "actions": [
                {
                    "action": "edit",
                    "allowed": true
                },
                {
                    "action": "create",
                    "allowed": true
                }
            ]
        },
        {
            "screen": "Rides",
            "allowed": true,
            "actions": [
                {
                    "action": "edit",
                    "allowed": true
                }
            ]
        },
        {
            "screen": "SalesHistory",
            "allowed": true,
            "actions": []
        },
        {
            "screen": "Transactions",
            "allowed": true,
            "actions": []
        },
        {
            "screen": "Suggestions",
            "allowed": true,
            "actions": [
                {
                    "action": "edit",
                    "allowed": true
                }
            ]
        },
        {
            "screen": "RideReports",
            "allowed": true,
            "actions": [
                {
                    "action": "edit",
                    "allowed": true
                }
            ]
        },
        {
            "screen": "Withdrawals",
            "allowed": true,
            "actions": [
                {
                    "action": "edit",
                    "allowed": true
                }
            ]
        },
        {
            "screen": "DocVerifications",
            "allowed": true,
            "actions": [
                {
                    "action": "edit",
                    "allowed": true
                }
            ]
        },
        {
            "screen": "SubscriptionPlans",
            "allowed": true,
            "actions": [
                {
                    "action": "create",
                    "allowed": true
                },
                {
                    "action": "edit",
                    "allowed": true
                }
            ]
        }
    ];


    const fetchUsers = async (pageNum = 1, search = '', append = false) => {
        try {
            setIsLoadingMore(append);
            const res = await getAdmins(pageNum, search);
            const newUsers = res.data.payload.all_imp_users;
            if (append) {
                setUsers(prev => [...prev, ...newUsers]);
            } else {
                setUsers(newUsers);
            }
            setTotalPages(res.data.payload.total_pages);
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setIsLoadingMore(false);
        }
    };

    const handleSearch = (searchValue) => {
        setDropdownSearchTerm(searchValue);
        setPage(1);
        fetchUsers(1, searchValue, false);
    };

    const handleLoadMore = () => {
        if (page < total_pages && !isLoadingMore) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchUsers(nextPage, dropdownSearchTerm, true);
        }
    };

    const handleUserSelect = (user) => {
        setSelectedUser(user._id);
        const userPermissions = user?.role_permissions_id?.permissions || [];

        // Ensure all screens from InitialPermission are represented
        const normalizedPermissions = InitialPermission.map(initialPerm => {
            const userPerm = userPermissions.find(p => p.screen === initialPerm.screen);
            if (userPerm) {
                return userPerm;
            } else {
                // Create default permission structure for screens not in user's permissions
                return {
                    screen: initialPerm.screen,
                    allowed: false,
                    actions: initialPerm.actions.map(action => ({
                        action: action.action,
                        allowed: false
                    }))
                };
            }
        });
        setPermissions(normalizedPermissions);
        // Set IP addresses if present
        setIpAddresses(user?.role_permissions_id?.ip_addresses || []);
        setIsDropdownOpen(false);
        setDropdownSearchTerm('');
    };

    const filteredUsers = users.filter(user =>
        user.username?.toLowerCase().includes(dropdownSearchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(dropdownSearchTerm.toLowerCase()) ||
        user.role?.toLowerCase().includes(dropdownSearchTerm.toLowerCase())
    );

    const selectedUserData = users.find(user => user._id === selectedUser);
    // Helper function to get action permission status
    const getActionAllowed = (screen, action) => {
        const userPerm = permissions.find(p => p.screen === screen);
        if (!userPerm) return false;

        if (action === 'view') {
            return userPerm.allowed;
        }

        const actionPerm = userPerm.actions?.find(a => a.action === action);
        return actionPerm ? actionPerm.allowed : false;
    };

    // Helper function to handle checkbox changes
    const handleCheckboxChange = (screen, action, checked) => {

        setPermissions(prev => {
            const updated = [...prev];
            let permIdx = updated.findIndex(p => p.screen === screen);

            if (permIdx === -1) {
                // Create new permission entry if it doesn't exist
                const initialPerm = InitialPermission.find(p => p.screen === screen);
                const newPerm = {
                    screen,
                    allowed: action === 'view' ? checked : false,
                    actions: initialPerm?.actions?.map(a => ({
                        action: a.action,
                        allowed: action === a.action ? checked : false
                    })) || []
                };

                // Add create action if it doesn't exist in initial actions
                if (action === 'create' && !newPerm.actions.find(a => a.action === 'create')) {
                    newPerm.actions.push({ action: 'create', allowed: checked });
                }

                updated.push(newPerm);
            } else {
                // Update existing permission
                if (action === 'view') {
                    updated[permIdx].allowed = checked;
                } else {
                    // Ensure actions array exists
                    if (!updated[permIdx].actions) {
                        updated[permIdx].actions = [];
                    }

                    let actIdx = updated[permIdx].actions.findIndex(a => a.action === action);
                    if (actIdx === -1) {
                        // Add new action if it doesn't exist
                        updated[permIdx].actions.push({ action, allowed: checked });
                    } else {
                        // Update existing action
                        updated[permIdx].actions[actIdx].allowed = checked;
                    }
                }
            }

            return updated;
        });
    };

    // Function to check if an action is available for a screen
    const isActionAvailable = (screen, action) => {
        const initialPerm = InitialPermission.find(p => p.screen === screen);
        if (action === 'view') return true; // View is always available
        if (action === 'edit') return initialPerm?.actions?.some(a => a.action === 'edit') || false;
        if (action === 'create') return initialPerm?.actions?.some(a => a.action === 'create') || false;
        return false;
    };

    const handleSavePermissions = async () => {
        if (!selectedUser || !selectedUserData) {
            alert('Please select a user first');
            return;
        }

        try {
            // Prepare the permissions data for API call
            const permissionsData = {
                user_id: selectedUser,
                permissions: permissions,
                ip_addresses: ipAddresses
            };


            // Call your API to update permissions
            await updatePermissions({ user_id: selectedUser, role_id: selectedUserData?.role_id?._id, permissions: permissions, ip_addresses: ipAddresses });

            toast.success('Permissions updated successfully!');
        } catch (error) {
            console.error('Error saving permissions:', error);
            toast.error('Failed to save permissions. Please try again.');
        }
    };

    // Helper to validate and add IP
    function handleAddIp() {
        const ip = ipInput.trim();
        if (!ip) return;
        // Simple IP address validation (IPv4)
        const ipRegex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

        if (!(ip === '*' || ipRegex.test(ip))) {
            toast.error('Invalid IP address');
            return;
        }

        if (ipAddresses.includes(ip)) {
            toast.info('IP address already added');
            return;
        }
        setIpAddresses([...ipAddresses, ip]);
        setIpInput("");
        setTimeout(() => ipInputRef.current?.focus(), 0);
    }

    useEffect(() => {
        fetchUsers(1, '');
    }, []);

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h6>Manage Permissions</h6>

                <div className="row w-50">
                    <div className="w-full">
                        <div className="position-relative">
                            {/* Custom Select Dropdown */}
                            <div
                                className={`form-select d-flex justify-content-between align-items-center ${isDropdownOpen ? 'show' : ''}`}
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                style={{ cursor: 'pointer', minHeight: '38px' }}
                            >
                                <span className={selectedUserData ? 'text-dark' : 'text-muted'}>
                                    {selectedUserData ? selectedUserData.username || selectedUserData.email : 'Select User'}
                                </span>
                            </div>

                            {/* Dropdown Menu */}
                            {isDropdownOpen && (
                                <div
                                    className="dropdown-menu show w-100 mt-1 shadow-lg"
                                    style={{
                                        position: 'absolute',
                                        zIndex: 1000,
                                        maxHeight: '300px',
                                        overflowY: 'auto'
                                    }}
                                >
                                    {/* Search Input */}
                                    <div className="p-2 border-bottom">
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Search users..."
                                            value={dropdownSearchTerm}
                                            onChange={(e) => handleSearch(e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </div>

                                    {/* Loading indicator for initial load */}
                                    {loading && users.length === 0 && (
                                        <div className="px-3 py-2 text-center text-muted">
                                            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                            Loading users...
                                        </div>
                                    )}

                                    {/* User Options */}
                                    {filteredUsers.length > 0 ? (
                                        <>
                                            {filteredUsers.map((user) => (
                                                <button
                                                    key={user.id}
                                                    type="button"
                                                    className={`dropdown-item d-flex align-items-center p-1 ${selectedUser === user.id ? 'active' : ''}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleUserSelect(user);
                                                    }}
                                                >
                                                    <div className="d-flex align-items-center me-2 p-1">
                                                        {user.avatar ? (
                                                            <img
                                                                src={user.avatar}
                                                                alt={user.username}
                                                                className="rounded-circle"
                                                                style={{ width: '24px', height: '24px' }}
                                                            />
                                                        ) : (
                                                            <div
                                                                className="rounded-circle bg-primary d-flex align-items-center justify-content-center text-white"
                                                                style={{ width: '24px', height: '24px', fontSize: '10px' }}
                                                            >
                                                                {(user.username || user.email || 'U').charAt(0).toUpperCase()}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <div className="fw-medium">
                                                            {user.username || user.email}
                                                        </div>
                                                        <div className="d-flex align-items-center">
                                                            {user.email && user.username && (
                                                                <small className="text-muted me-2">{user.email}</small>
                                                            )}
                                                            {user.role && (
                                                                <span className="badge bg-secondary">{user.role}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {selectedUser === user.id && (
                                                        <Check className="text-success ms-2" size={16} />
                                                    )}
                                                </button>
                                            ))}

                                            {/* Load More Button */}
                                            {page < total_pages && (
                                                <div className="px-3 py-2 border-top">
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-primary btn-sm w-100"
                                                        onClick={handleLoadMore}
                                                        disabled={isLoadingMore}
                                                    >
                                                        {isLoadingMore ? (
                                                            <>
                                                                <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                                                Loading...
                                                            </>
                                                        ) : (
                                                            `Load More (${page}/${total_pages})`
                                                        )}
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="px-3 py-2 text-center text-muted">
                                            {dropdownSearchTerm ? 'No users found' : 'No users available'}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Click outside to close dropdown */}
                {isDropdownOpen && (
                    <div
                        className="position-fixed w-100 h-100"
                        style={{
                            top: 0,
                            left: 0,
                            zIndex: 999,
                            background: 'transparent'
                        }}
                        onClick={() => setIsDropdownOpen(false)}
                    />
                )}
            </div>

            {/* IP Address Chip Input UI */}
            {selectedUser && <div className="mb-4">
                <label className="form-label fw-medium">Allowed IP Addresses</label>
                <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                    {ipAddresses.map((ip, idx) => (
                        <span key={ip + idx} className="badge bg-primary d-flex align-items-center" style={{ fontSize: '1rem', padding: '0.5em 0.75em', marginRight: 4 }}>
                            {ip}
                            <button
                                type="button"
                                className="btn btn-sm btn-link text-white ms-2 p-0"
                                style={{ lineHeight: 1 }}
                                onClick={() => setIpAddresses(ipAddresses.filter((_, i) => i !== idx))}
                                tabIndex={-1}
                            >
                                <X size={16} />
                            </button>
                        </span>
                    ))}
                    <input
                        ref={ipInputRef}
                        type="text"
                        className="form-control form-control-sm"
                        style={{ width: 180, minWidth: 120, display: 'inline-block' }}
                        placeholder="Add IP address"
                        value={ipInput}
                        onChange={e => setIpInput(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ',') {
                                e.preventDefault();
                                handleAddIp();
                            }
                        }}
                    />
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm ms-2"
                        onClick={handleAddIp}
                    >
                        Add
                    </button>
                </div>
                <small className="text-muted">Press Enter or click Add to add an IP. Click × to remove.</small>
            </div>
            }

            <div className="row mt-3">
                <div className="col-md-12">
                    <div className="card">
                        <div className="card-body">
                            {selectedUser ? (
                                <div className="table-responsive">
                                    <table className="table table-bordered table-hover align-middle">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Screen</th>
                                                <th className="text-center">View</th>
                                                <th className="text-center">Edit</th>
                                                <th className="text-center">Create</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {InitialPermission.map((permission) => (
                                                <tr key={permission.screen}>
                                                    <td className="fw-medium">{permission.screen}</td>
                                                    <td className="text-center">
                                                        <div
                                                            className={`custom-checkbox ${getActionAllowed(permission.screen, 'view') ? 'checked' : 'unchecked'}`}
                                                            onClick={() => handleCheckboxChange(permission.screen, 'view', !getActionAllowed(permission.screen, 'view'))}
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                width: '20px',
                                                                height: '20px',
                                                                border: '2px solid #b5b5b5',
                                                                borderRadius: '4px',
                                                                backgroundColor: getActionAllowed(permission.screen, 'view') ? '#28a745' : 'transparent',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.2s ease',
                                                                userSelect: 'none'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                if (!getActionAllowed(permission.screen, 'view')) {
                                                                    e.target.style.borderColor = '#6c757d';
                                                                }
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                if (!getActionAllowed(permission.screen, 'view')) {
                                                                    e.target.style.borderColor = '#b5b5b5';
                                                                }
                                                            }}
                                                        >
                                                            {getActionAllowed(permission.screen, 'view') && (
                                                                <Check size={12} color="white" strokeWidth={3} />
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        {isActionAvailable(permission.screen, 'edit') ? (
                                                            <div
                                                                className={`custom-checkbox ${getActionAllowed(permission.screen, 'edit') ? 'checked' : 'unchecked'}`}
                                                                onClick={() => handleCheckboxChange(permission.screen, 'edit', !getActionAllowed(permission.screen, 'edit'))}
                                                                style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    width: '20px',
                                                                    height: '20px',
                                                                    border: '2px solid #b5b5b5',
                                                                    borderRadius: '4px',
                                                                    backgroundColor: getActionAllowed(permission.screen, 'edit') ? '#28a745' : 'transparent',
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.2s ease',
                                                                    userSelect: 'none'
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    if (!getActionAllowed(permission.screen, 'edit')) {
                                                                        e.target.style.borderColor = '#6c757d';
                                                                    }
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    if (!getActionAllowed(permission.screen, 'edit')) {
                                                                        e.target.style.borderColor = '#b5b5b5';
                                                                    }
                                                                }}
                                                            >
                                                                {getActionAllowed(permission.screen, 'edit') && (
                                                                    <Check size={12} color="white" strokeWidth={3} />
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        {isActionAvailable(permission.screen, 'create') ? (
                                                            <div
                                                                className={`custom-checkbox ${getActionAllowed(permission.screen, 'create') ? 'checked' : 'unchecked'}`}
                                                                onClick={() => handleCheckboxChange(permission.screen, 'create', !getActionAllowed(permission.screen, 'create'))}
                                                                style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    width: '20px',
                                                                    height: '20px',
                                                                    border: '2px solid #b5b5b5',
                                                                    borderRadius: '4px',
                                                                    backgroundColor: getActionAllowed(permission.screen, 'create') ? '#28a745' : 'transparent',
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.2s ease',
                                                                    userSelect: 'none'
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    if (!getActionAllowed(permission.screen, 'create')) {
                                                                        e.target.style.borderColor = '#6c757d';
                                                                    }
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    if (!getActionAllowed(permission.screen, 'create')) {
                                                                        e.target.style.borderColor = '#b5b5b5';
                                                                    }
                                                                }}
                                                            >
                                                                {getActionAllowed(permission.screen, 'create') && (
                                                                    <Check size={12} color="white" strokeWidth={3} />
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-5 d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
                                    <div className="text-muted">
                                        <div className="mb-3">
                                            <i className="fas fa-user-shield fa-3x text-muted"></i>
                                        </div>
                                        <h5>Select a User</h5>
                                        <p>Please select a user from the dropdown above to manage their permissions.</p>
                                    </div>
                                </div>
                            )}

                            {selectedUser && (
                                <div className="d-flex justify-content-end mt-3">
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleSavePermissions}
                                        disabled={!selectedUser || loading}
                                    >
                                        {loading ? (
                                            <>
                                                <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                                Saving...
                                            </>
                                        ) : (
                                            'Save Permissions'
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default RolePermissionModule;